/**
 * ContractSendingIdentitySettings — Settings → Contracts sending-identity + invite-template config
 * (CB-8.3, FR-72 / FR-74). The FR-72 half of the Settings → Contracts surface; the FR-70 feature
 * gate is the sibling ContractAccessSettings.tsx in the same tab.
 *
 * Pure CONFIG over EXISTING data — no new infra, no send path, no template engine:
 *   - Sending domain: the existing `tenants.email_domain` (same column EmailDomainConfiguration
 *     edits). The resolved `contracts@{domain}` is exactly what CB-4.1 sends from.
 *   - From-name: the existing `tenants.email_config.from_name` (same field EmailConfiguration edits);
 *     persisted by merging into email_config so the SMTP config is preserved untouched.
 *   - Invite template: the existing per-tenant `contract_invite` row in `notification_templates`
 *     (seeded by CB-4.2's migration). Edits to subject/body are what CB-4.2's send.py renders next.
 *
 * Multi-tenant rule: every read/write is scoped to the active tenant; the notification_templates
 * query ALWAYS filters by tenant_id + notification_type (never by type alone — would cross tenants).
 */
import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Save, Mail, Info, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { useTenant } from '@/contexts/TenantContext';
import {
  resolveContractFromAddress,
  findMissingRequiredMergeFields,
} from '@/lib/contractInviteTemplate';

const INVITE_TYPE = 'contract_invite';

export function ContractSendingIdentitySettings() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Sending identity
  const [emailDomain, setEmailDomain] = useState('');
  const [fromName, setFromName] = useState('');
  // Preserve the rest of email_config (SMTP etc.) on save — we only touch from_name.
  const [emailConfig, setEmailConfig] = useState<Record<string, unknown> | null>(null);

  // Invite template
  const [templateExists, setTemplateExists] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [variables, setVariables] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!tenantId) return;
      setLoading(true);
      setLoadError(false);
      try {
        const { data: tenantRow, error: tErr } = await supabase
          .from('tenants')
          .select('email_domain, email_config')
          .eq('tenant_id', tenantId)
          .single();
        if (tErr) throw tErr;

        const { data: tmplRows, error: nErr } = await supabase
          .from('notification_templates')
          .select('email_subject_template, email_body_template, variables')
          .eq('notification_type', INVITE_TYPE)
          .eq('tenant_id', tenantId) // multi-tenant rule: never by notification_type alone
          .eq('is_active', true)
          .limit(1);
        if (nErr) throw nErr;

        if (cancelled) return;
        const cfg = (tenantRow?.email_config as Record<string, unknown> | null) ?? null;
        setEmailConfig(cfg);
        setEmailDomain((tenantRow?.email_domain as string) || '');
        setFromName((cfg?.from_name as string) || '');

        const tmpl = tmplRows?.[0];
        setTemplateExists(!!tmpl);
        setSubject((tmpl?.email_subject_template as string) || '');
        setBody((tmpl?.email_body_template as string) || '');
        setVariables((tmpl?.variables as string[]) || []);
      } catch (error) {
        console.error('[ContractSendingIdentitySettings] load failed:', error);
        if (!cancelled) {
          setLoadError(true); // guard saves — never write a merge built off a failed/partial load
          toast.error('Failed to load contract sending settings');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const saveIdentity = async () => {
    if (!tenantId) return;
    // Never persist a merge built off a null/partial snapshot — that would clobber existing
    // email_config (SMTP/provider). If the load failed, the admin must reload first.
    if (loadError) {
      toast.error('Reload the page before saving — settings could not be loaded.');
      return;
    }
    setSavingIdentity(true);
    try {
      const { error } = await supabase
        .from('tenants')
        .update({
          email_domain: emailDomain.trim() || null,
          // Merge — preserve SMTP/provider/etc.; only from_name changes.
          email_config: { ...(emailConfig ?? {}), from_name: fromName },
        })
        .eq('tenant_id', tenantId);
      if (error) throw error;
      setEmailConfig((prev) => ({ ...(prev ?? {}), from_name: fromName }));
      toast.success('Contract sending identity saved');
    } catch (error) {
      console.error('[ContractSendingIdentitySettings] saveIdentity failed:', error);
      toast.error('Failed to save sending identity');
    } finally {
      setSavingIdentity(false);
    }
  };

  const saveTemplate = async () => {
    if (!tenantId) return;
    setSavingTemplate(true);
    try {
      const { error } = await supabase
        .from('notification_templates')
        .update({ email_subject_template: subject, email_body_template: body })
        .eq('notification_type', INVITE_TYPE) // multi-tenant rule: paired with tenant_id below
        .eq('tenant_id', tenantId);
      if (error) throw error;
      toast.success('Invite email template saved');
    } catch (error) {
      console.error('[ContractSendingIdentitySettings] saveTemplate failed:', error);
      toast.error('Failed to save invite template');
    } finally {
      setSavingTemplate(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const fromAddress = resolveContractFromAddress(emailDomain);
  const missingRequired = findMissingRequiredMergeFields(subject, body);

  return (
    <div className="space-y-6">
      {loadError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-sm">
            Couldn't load your contract sending settings. Reload the page before making changes —
            saving now is disabled to avoid overwriting your existing email configuration.
          </AlertDescription>
        </Alert>
      )}

      {/* ── Sending identity ───────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Contract Sending Identity
          </CardTitle>
          <CardDescription>
            The domain and name your signing invites are sent from. Uses your tenant email settings —
            DNS setup for a custom domain lives under Settings → Notifications → Email Domain.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Contracts are sent from</Label>
            <div className="p-3 bg-muted rounded-lg font-mono text-sm">{fromAddress}</div>
            <p className="text-xs text-muted-foreground">
              Resolved from your sending domain below (defaults to the platform domain when empty).
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contract-from-name">From name</Label>
            <Input
              id="contract-from-name"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Acme Solar"
            />
            <p className="text-xs text-muted-foreground">
              Shown as the sender name on contract emails. Shared with your tenant email settings.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contract-sending-domain">Sending domain (optional)</Label>
            <Input
              id="contract-sending-domain"
              value={emailDomain}
              onChange={(e) => setEmailDomain(e.target.value)}
              placeholder="mail.joulescrm.com"
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use the platform default. Custom domains must be verified in Postmark
              (see Settings → Notifications → Email Domain for the DNS records).
            </p>
          </div>

          <Button onClick={saveIdentity} disabled={savingIdentity || loadError}>
            {savingIdentity ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Sending Identity
          </Button>
        </CardContent>
      </Card>

      {/* ── Invite email template ──────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Invite Email Template</CardTitle>
          <CardDescription>
            The email recipients receive when you send a contract for signature. Edits apply to the
            next invite sent.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {!templateExists ? (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                No custom invite template exists for this tenant yet — sent invites use the built-in
                default. The customizable template is created the first time one is seeded for your
                tenant.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="invite-subject">Subject</Label>
                <Input
                  id="invite-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite-body">Body (HTML)</Label>
                <Textarea
                  id="invite-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  className="font-mono text-xs"
                />
              </div>

              {variables.length > 0 && (
                <div className="space-y-2">
                  <Label>Available merge fields</Label>
                  <div className="flex flex-wrap gap-2">
                    {variables.map((v) => (
                      <code
                        key={v}
                        className="rounded bg-muted px-2 py-1 text-xs text-foreground/80"
                      >
                        {`{${v}}`}
                      </code>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Keep <code className="text-xs">{'{signing_link}'}</code> — it's each recipient's
                    unique link to sign.
                  </p>
                </div>
              )}

              {missingRequired.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    Warning: the template is missing{' '}
                    {missingRequired.map((v) => `{${v}}`).join(', ')}. Recipients may not be able to
                    reach the signing page. You can still save, but adding it back is strongly
                    recommended.
                  </AlertDescription>
                </Alert>
              )}

              <Button onClick={saveTemplate} disabled={savingTemplate || loadError}>
                {savingTemplate ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Invite Template
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
